import type { Encoding } from "@/types/api";
import { sourceEncodingIsChosen } from "@/utils/providerCapabilities";
import {
  errorCountLabel,
  jsonErrorRows,
  schemaTextProblem,
} from "./editorErrors";
import type { LintStatus } from "./pipelineLint";
import { isBlankStoredScript, scriptDraftErrorCount } from "./scriptErrors";
import { sourceHasSchema } from "./sourceDeliversState";
import {
  SCHEMA_EDITOR_ID,
  type StagedResource,
  type StagedSchema,
  type StageTabId,
  type StudioView,
  schemaEditorId,
  schemaIdOfInstance,
  schemaInstanceId,
  unattachedSchemaEditorId,
} from "./stagingModel";

// What stands between the user and Deploy (Figma DropdownMenu
// 14669-190836, the "Unresolved" list): every editor with an error, plus
// the Full view's lint verdicts. With problems the header shows the
// segmented pair — Deploy DISABLED next to the "Unresolved (N)" button
// whose menu lists them (UnresolvedDeployButton).

export type ProblemTarget =
  | { kind: "input" }
  | { kind: "schema"; rowId: string; tab: StageTabId }
  | { kind: "script"; rowId: string; tab: "transform" }
  | { kind: "code" };

export interface DeployProblem {
  id: string;
  label: string;
  target: ProblemTarget;
  /** A check that hasn't settled yet, not a verdict: lint still running,
   *  services still loading. Deploy stays gated, but the header shows a
   *  wait, not an error (onlyChecking / DeployButton). */
  checking?: true;
}

export interface DeployProblemInput {
  studioView: StudioView;
  lintStatus: LintStatus;
  /** The Full view's error rows, as the status bar counts them. */
  codeErrorCount: number;
  inputDocument: string;
  /** The source's chosen `input_encoding` by staged row id
   *  (pipelineExtras.inputEncodings, TTTL-268). */
  inputEncodings?: Record<string, Encoding>;
  /** Unsaved schema texts by editor row id (stagingDrafts). */
  schemaDrafts: Record<string, string>;
  /** Script texts by transform row id (stagingDrafts) — the untitled
   *  drafts and the edited stored scripts; a stored script never opened
   *  has no draft and is not checked here. */
  scriptDrafts: Record<string, string>;
  schemas: StagedSchema[];
  /** Stored definitions by staged schema id (useSchemaDefinitions): the
   *  text, null when it can't be fetched (nothing to verify, not gated),
   *  absent while the fetch is in flight (gated as a check). A saved
   *  schema deploys as stored, so its text counts wherever no open draft
   *  stands in for it (TTTL-216). */
  schemaDefinitions: Record<string, string | null>;
  /** Full view only: the stored schemas the code's schemas block names,
   *  as the code names them — the Full view deploys its code, whose
   *  references may not be staged yet (a pipeline opened straight into
   *  it). Same text convention as schemaDefinitions; undefined while the
   *  fetch is in flight. */
  codeSchemas: Array<{
    serviceName: string;
    name: string;
    text: string | null | undefined;
  }>;
  /** Staged source/sink ids whose drop-time schema seed is still
   *  resolving (schemaSeeding): their slot is empty only for the moment,
   *  so a deploy now would ship the pipeline without the schema about to
   *  land — gated as a check until the seed settles. */
  seedingStageIds: readonly string[];
  stages: {
    sources: StagedResource[];
    transforms: StagedResource[];
    sinks: StagedResource[];
  };
}

const TAB_TITLES: Record<StageTabId, string> = {
  source: "Source",
  transform: "Transform",
  sink: "Sink",
  tests: "Tests",
};

// A block key is a schema editor row id: the bare sink slot, a tab's
// bottom slot ("@role") or a stage's slot (the stage id).
function blockOf(
  key: string,
  stages: DeployProblemInput["stages"],
): { tab: StageTabId; stageName: string | null } {
  const rest = key.slice(SCHEMA_EDITOR_ID.length + 1);
  if (key === SCHEMA_EDITOR_ID) return { tab: "sink", stageName: null };
  if (rest.startsWith("@")) {
    return { tab: rest.slice(1) as StageTabId, stageName: null };
  }
  const lists: Array<[StageTabId, StagedResource[]]> = [
    ["source", stages.sources],
    ["transform", stages.transforms],
    ["sink", stages.sinks],
  ];
  for (const [tab, list] of lists) {
    const stage = list.find((s) => s.id === rest);
    if (stage) return { tab, stageName: stage.name };
  }
  return { tab: "sink", stageName: null };
}

function schemaProblem(
  rowId: string,
  input: DeployProblemInput,
): DeployProblem {
  const schemaId = schemaIdOfInstance(rowId);
  const blockKey = schemaId === null ? rowId : rowId.slice(schemaId.length + 1);
  const block = blockOf(blockKey, input.stages);
  const schemaName = input.schemas.find((s) => s.id === schemaId)?.name;
  const subject = schemaName
    ? `The schema ${schemaName}`
    : block.stageName
      ? `The schema of ${block.stageName}`
      : `The ${TAB_TITLES[block.tab]} schema`;
  return {
    id: `schema:${rowId}`,
    label: `${subject} has an error`,
    target: { kind: "schema", rowId, tab: block.tab },
  };
}

// The Full view deploys its code draft, so its lint verdict counts there;
// the Stages view deploys the panels, whose last verdict may be stale.
function codeProblem(input: DeployProblemInput): DeployProblem | null {
  if (input.studioView !== "full") return null;
  switch (input.lintStatus) {
    case "errors":
      return {
        id: "code",
        label: `The pipeline code has ${errorCountLabel(input.codeErrorCount)}`,
        target: { kind: "code" },
      };
    case "placeholders":
      return {
        id: "code",
        label: "The pipeline has placeholders to fill in",
        target: { kind: "code" },
      };
    case "pending":
      // From the first beat: the reference checks take up to a second
      // after a view switch, and an invalid pipeline must not slip out in
      // that window. Rendered as a wait, not the error pair.
      return {
        id: "code",
        label: "Pipeline checks are still running",
        target: { kind: "code" },
        checking: true,
      };
    default:
      return null;
  }
}

/** The stage tabs the problems point at — the tab bar marks them with a
 *  dot while the Unresolved list shows. Code problems live in the Full
 *  view and mark no tab. */
export function tabsWithProblems(
  problems: readonly DeployProblem[],
): Set<StageTabId> {
  const tabs = new Set<StageTabId>();
  for (const { target } of problems) {
    if (target.kind === "input") tabs.add("source");
    if (target.kind === "schema" || target.kind === "script")
      tabs.add(target.tab);
  }
  return tabs;
}

/** Nothing but unsettled checks in the list: the Deploy control shows a
 *  disabled wait instead of the error-styled Unresolved chip. */
export function onlyChecking(problems: readonly DeployProblem[]): boolean {
  return problems.length > 0 && problems.every((p) => p.checking);
}

// The editor rows a staged schema occupies: one per stage it is attached
// to, plus the bottom block of the tab it waits in.
function schemaBlockKeys(schema: StagedSchema): string[] {
  const keys = schema.attachments.map((a) => schemaEditorId(a.id));
  for (const tab of schema.pending ?? []) {
    keys.push(unattachedSchemaEditorId(tab));
  }
  return keys;
}

// Stored schemas deploy as stored: every block whose schema shows no open
// draft is checked against the stored text instead. A definition still
// loading gates as a check; one that failed to load can't be verified
// and passes.
function storedSchemaProblems(input: DeployProblemInput): {
  problems: DeployProblem[];
  loading: boolean;
} {
  const problems: DeployProblem[] = [];
  let loading = false;
  for (const schema of input.schemas) {
    for (const blockKey of schemaBlockKeys(schema)) {
      const rowId = schemaInstanceId(schema.id, blockKey);
      if (input.schemaDrafts[rowId] != null) continue;
      if (!(schema.id in input.schemaDefinitions)) {
        loading = true;
        continue;
      }
      const text = input.schemaDefinitions[schema.id];
      if (text !== null && schemaTextProblem(text) !== null) {
        problems.push(schemaProblem(rowId, input));
      }
    }
  }
  return { problems, loading };
}

// The Full view's stored-schema check: the code's schema references,
// by the name the code uses (no editor row to point at — the code view
// is the target).
function codeSchemaProblems(input: DeployProblemInput): {
  problems: DeployProblem[];
  loading: boolean;
} {
  const problems: DeployProblem[] = [];
  let loading = false;
  if (input.studioView !== "full") return { problems, loading };
  // Names are unique only within one service's store (TTTL-319): the
  // same name on two services is two schemas, each checked once.
  const seen = new Set<string>();
  for (const { serviceName, name, text } of input.codeSchemas) {
    const key = `${serviceName}:${name}`;
    if (seen.has(key)) continue;
    seen.add(key);
    if (text === undefined) {
      loading = true;
      continue;
    }
    if (text !== null && schemaTextProblem(text) !== null) {
      problems.push({
        id: `code-schema:${key}`,
        label: `The schema ${name} has an error`,
        target: { kind: "code" },
      });
    }
  }
  return { problems, loading };
}

// The Stages "Source delivers" control can pick Avro before a schema is
// attached; the engine cannot decode such a source (TTTL-268). Stages only:
// the Full view's lint has the same rule, and its rows may be stale there.
// Only where the encoding is the user's choice — fixed providers ignore it.
function sourceDeliversProblem(
  input: DeployProblemInput,
): DeployProblem | null {
  if (input.studioView !== "stages") return null;
  const source = input.stages.sources[0];
  if (!source || !deliversAvroWithoutSchema(source, input)) return null;
  return {
    id: "source-delivers",
    label: "The source delivers Avro but has no schema",
    target: { kind: "input" },
  };
}

function deliversAvroWithoutSchema(
  source: StagedResource,
  input: DeployProblemInput,
): boolean {
  if (input.inputEncodings?.[source.id] !== "avro") return false;
  if (!sourceEncodingIsChosen(source.provider)) return false;
  return !sourceHasSchema(input.schemas, source.id);
}

function inputProblem(input: DeployProblemInput): DeployProblem | null {
  if (jsonErrorRows(input.inputDocument).length === 0) return null;
  return {
    id: "input",
    label: "The input document has an error",
    target: { kind: "input" },
  };
}

function draftSchemaProblems(input: DeployProblemInput): DeployProblem[] {
  return Object.entries(input.schemaDrafts)
    .filter(([, text]) => jsonErrorRows(text).length > 0)
    .map(([rowId]) => schemaProblem(rowId, input));
}

// One per script draft with errors, in staging order (untitled drafts are
// transform rows too); a stored script never opened has no draft.
function scriptProblems(input: DeployProblemInput): DeployProblem[] {
  const problems: DeployProblem[] = [];
  for (const stage of input.stages.transforms) {
    const text = input.scriptDrafts[stage.id];
    const count = text == null ? 0 : scriptDraftErrorCount(stage.id, text);
    if (count === 0) continue;
    const label =
      text != null && isBlankStoredScript(stage.id, text)
        ? `The script ${stage.name} is empty`
        : `The script ${stage.name} has ${errorCountLabel(count)}`;
    problems.push({
      id: `script:${stage.id}`,
      label,
      target: { kind: "script", rowId: stage.id, tab: "transform" },
    });
  }
  return problems;
}

// The stored-schema checks (Stages rows and the Full view's code), plus
// one wait row while any definition is still loading.
function storedAndCodeSchemaProblems(
  input: DeployProblemInput,
): DeployProblem[] {
  const stored = storedSchemaProblems(input);
  const inCode = codeSchemaProblems(input);
  const problems = [...stored.problems, ...inCode.problems];
  if (stored.loading || inCode.loading) {
    problems.push({
      id: "schemas",
      label: "Checking the attached schemas",
      target: { kind: "code" },
      checking: true,
    });
  }
  return problems;
}

/** The dropped stages whose schema seed is still resolving, as staged —
 *  a seeding id that already left staging (unstaged mid-seed) is dropped.
 *  Shared by Deploy's problems list and the toolbar's Stages → Full
 *  switch, which waits on the same seeds: the Full view's code is
 *  generated from staging, and an edit made there would be thrown away
 *  when the landing seed regenerates it. */
export function seedingWaits(
  seedingStageIds: readonly string[],
  stages: { sources: StagedResource[]; sinks: StagedResource[] },
): SeedingWait[] {
  return seedingStageIds.flatMap((id): SeedingWait[] => {
    const source = stages.sources.find((s) => s.id === id);
    if (source) return [{ id, name: source.name, tab: "source" }];
    const sink = stages.sinks.find((s) => s.id === id);
    if (sink) return [{ id, name: sink.name, tab: "sink" }];
    return [];
  });
}

export interface SeedingWait {
  id: string;
  name: string;
  tab: StageTabId;
}

/** What a seeding stage's wait says, on Deploy and on the view switch. */
export function seedingWaitLabel(wait: { name: string }): string {
  return `Loading the schema for ${wait.name}`;
}

// One wait per dropped stage whose schema seed is still resolving, aimed
// at that stage's SCHEMA slot.
function seedingProblems(input: DeployProblemInput): DeployProblem[] {
  return seedingWaits(input.seedingStageIds, input.stages).map((wait) => ({
    id: `seed:${wait.id}`,
    label: seedingWaitLabel(wait),
    target: { kind: "schema", rowId: schemaEditorId(wait.id), tab: wait.tab },
    checking: true,
  }));
}

export function deployProblems(input: DeployProblemInput): DeployProblem[] {
  const singles = [
    codeProblem(input),
    inputProblem(input),
    sourceDeliversProblem(input),
  ].filter((p): p is DeployProblem => p !== null);
  return [
    ...singles,
    ...seedingProblems(input),
    ...draftSchemaProblems(input),
    ...storedAndCodeSchemaProblems(input),
    ...scriptProblems(input),
  ];
}
